import { useEffect, useState } from "react";
import { masterApi, apiError } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { modal } from "@/lib/modal";
import { Database, Download, Upload, RefreshCw, ChevronLeft, ChevronRight, Loader2, AlertTriangle, ArchiveRestore, HardDriveDownload, Mail, Send, Clock } from "lucide-react";
import { Switch } from "@/components/ui/switch";

const inputCls = "w-full h-10 rounded-lg bg-white border border-neutral-200 px-3 text-sm outline-none focus:border-[color:var(--brand-blue)] focus:ring-2 focus:ring-blue-500/15 transition";

const LABELS = {
  clientes: "Clientes", atendimentos: "Atendimentos", caixas: "Caixas", caixa: "Caixa",
  frentistas: "Vendedores", unidades: "Unidades", precos: "Preços",
  precos_app_driver: "Preços · App Driver", config_descontos: "Descontos (Normal)",
  config_descontos_app_driver: "Descontos (App Driver)", otps: "OTPs",
  logs_acesso: "Logs de Acesso", whatsapp_envios: "WhatsApp · Envios",
  campanhas: "Campanhas", campanha_execucoes: "Execuções de Campanha",
  pesquisas: "Pesquisas", pesquisa_respostas: "Respostas de Pesquisa", indicacoes: "Indicações",
  master_config: "Config · Master", whatsapp_config: "Config · WhatsApp",
};
const lbl = (t) => LABELS[t] || t;

function fmtBytes(n) {
  if (!n) return "—";
  const u = ["B", "KB", "MB", "GB"]; let i = 0, v = n;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}

export default function MasterBackup() {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [gerando, setGerando] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [confirmacao, setConfirmacao] = useState("");
  const [restaurando, setRestaurando] = useState(false);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [ag, setAg] = useState(null);
  const [agForm, setAgForm] = useState({ ativo: false, email_destino: "", frequencia: "diaria", hora: 3, minuto: 0, dia_semana: 0, dia_mes: 1, smtp_host: "", smtp_porta: 587, smtp_user: "", smtp_senha: "", smtp_from_nome: "" });
  const [savingAg, setSavingAg] = useState(false);
  const [enviandoAg, setEnviandoAg] = useState(false);
  const setAgF = (k, v) => setAgForm((f) => ({ ...f, [k]: v }));

  const loadInfo = async () => {
    setLoadingInfo(true);
    try { const { data } = await masterApi.get("/master/backup/info"); setInfo(data); }
    catch (e) { modal.error(apiError(e)); } finally { setLoadingInfo(false); }
  };
  const loadLogs = async (p = 1) => {
    try {
      const { data } = await masterApi.get(`/master/backup/logs?page=${p}&page_size=15`);
      setLogs(data.items || []); setPage(data.page || p);
      setTotalPages(data.total_pages || 0); setTotal(data.total || 0);
    } catch (e) { modal.error(apiError(e)); }
  };
  const loadAg = async () => {
    try {
      const { data } = await masterApi.get("/master/backup/agendamento");
      setAg(data);
      setAgForm({ ativo: data.ativo, email_destino: data.email_destino || "", frequencia: data.frequencia || "diaria",
        hora: data.hora ?? 3, minuto: data.minuto ?? 0, dia_semana: data.dia_semana ?? 0, dia_mes: data.dia_mes ?? 1,
        smtp_host: data.smtp_host || "", smtp_porta: data.smtp_porta ?? 587, smtp_user: data.smtp_user || "",
        smtp_senha: "", smtp_from_nome: data.smtp_from_nome || "" });
    } catch (e) { /* silencioso */ }
  };
  useEffect(() => { loadInfo(); loadLogs(1); loadAg(); }, []);

  const saveAg = async () => {
    if (agForm.ativo && !agForm.email_destino.trim()) { modal.error("Informe o e-mail destino."); return; }
    setSavingAg(true);
    try {
      await masterApi.put("/master/backup/agendamento", agForm);
      modal.success("Agendamento salvo.");
      loadAg();
    } catch (e) { modal.error(apiError(e)); } finally { setSavingAg(false); }
  };

  const enviarAgora = async () => {
    if (!agForm.email_destino.trim()) { modal.error("Informe o e-mail destino e salve antes."); return; }
    setEnviandoAg(true);
    try {
      const { data } = await masterApi.post("/master/backup/enviar-agora");
      modal.success(data.detail || "Backup enviado por e-mail.");
      loadAg(); loadLogs(1);
    } catch (e) { modal.error(apiError(e)); } finally { setEnviandoAg(false); }
  };

  const gerarBaixar = async () => {
    setGerando(true);
    try {
      const resp = await masterApi.get("/master/backup/download", { responseType: "blob" });
      const cd = resp.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const nome = m ? m[1] : `backup_postos_${Date.now()}.zip`;
      const url = window.URL.createObjectURL(new Blob([resp.data], { type: "application/zip" }));
      const a = document.createElement("a"); a.href = url; a.download = nome;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      modal.success("Backup completo gerado e baixado!");
      loadLogs(1); loadInfo();
    } catch (e) { modal.error(apiError(e)); } finally { setGerando(false); }
  };

  const restaurar = async () => {
    if (!arquivo) { modal.error("Selecione o arquivo .zip do backup."); return; }
    if (confirmacao.trim().toUpperCase() !== "RESTAURAR") { modal.error("Digite RESTAURAR para liberar."); return; }
    const ok = await modal.confirm({
      title: "Restaurar backup — ação destrutiva",
      message: `Isto vai SUBSTITUIR todos os dados atuais pelos do arquivo "${arquivo.name}".`,
      details: ["Um backup de segurança automático é gerado antes do restore.",
                "Recomendado fazer em horário de baixo movimento."],
      confirmText: "Sim, restaurar agora", cancelText: "Cancelar", tone: "danger",
    });
    if (!ok) return;
    setRestaurando(true);
    try {
      const { data } = await masterApi.post(
        "/master/backup/restaurar?confirmar=RESTAURAR",
        arquivo,
        { headers: { "Content-Type": "application/zip" } }
      );
      modal.success(`Restauração concluída — ${data.total_registros} registro(s) restaurado(s).`);
      setArquivo(null); setConfirmacao("");
      loadInfo(); loadLogs(1);
    } catch (e) { modal.error(apiError(e)); } finally { setRestaurando(false); }
  };

  return (
    <div className="px-4 md:px-6 pt-4 pb-8 space-y-4 float-in" data-testid="master-backup">
      <div>
        <div className="chip mb-1.5 text-[10px] py-0.5"><Database size={11} /> Sistema · Backup</div>
        <h1 className="font-serif-display text-2xl md:text-3xl leading-[1.1]">Backup &amp; Restauração</h1>
      </div>

      {/* Quantidades gerais */}
      <section className="card-soft rounded-2xl p-5" data-testid="bk-quantidades">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <HardDriveDownload size={18} className="text-[color:var(--brand-blue)]" />
            <h2 className="font-serif-display text-lg">Quantidades gerais</h2>
          </div>
          <button onClick={loadInfo} disabled={loadingInfo} data-testid="bk-refresh-info"
                  className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5">
            <RefreshCw size={13} className={loadingInfo ? "animate-spin" : ""} /> Atualizar
          </button>
        </div>
        {!info ? <div className="text-sm text-neutral-500">Carregando...</div> : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {info.tabelas.map((t) => (
                <div key={t.tabela} className="rounded-lg bg-neutral-50 p-2.5 flex items-center justify-between" data-testid={`bk-cnt-${t.tabela}`}>
                  <span className="text-[12px] text-neutral-600 truncate pr-2">{lbl(t.tabela)}</span>
                  <span className="font-serif-display text-base">{t.registros.toLocaleString("pt-BR")}</span>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              <Mini label="Tabelas" value={String(info.total_tabelas)} />
              <Mini label="Total de registros" value={info.total_registros.toLocaleString("pt-BR")} accent />
            </div>
          </>
        )}
      </section>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Gerar backup */}
        <section className="card-soft rounded-2xl p-5 border-l-4 border-emerald-400" data-testid="bk-gerar">
          <div className="flex items-center gap-2 mb-1">
            <Download size={18} className="text-emerald-600" />
            <h2 className="font-serif-display text-lg">Gerar backup completo</h2>
          </div>
          <p className="text-[12px] text-neutral-500 mb-3">
            Exporta <b>todas as tabelas e cadastros</b> em um arquivo <b>.zip</b> e faz o download.
          </p>
          <button onClick={gerarBaixar} disabled={gerando} data-testid="bk-gerar-btn" className="btn-action btn-salvar">
            {gerando ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
            {gerando ? "Gerando..." : "Gerar e baixar backup"}
          </button>
        </section>

        {/* Restaurar */}
        <section className="card-soft rounded-2xl p-5 border-l-4 border-rose-400" data-testid="bk-restaurar">
          <div className="flex items-center gap-2 mb-1">
            <ArchiveRestore size={18} className="text-rose-600" />
            <h2 className="font-serif-display text-lg">Restaurar backup</h2>
          </div>
          <div className="flex items-start gap-2 text-[11px] text-rose-800 bg-rose-50 border border-rose-200 rounded-lg p-2 mb-3">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>Substitui <b>todos os dados atuais</b>. Um backup de segurança é gerado automaticamente antes.</span>
          </div>
          <input type="file" accept=".zip" data-testid="bk-file"
                 onChange={(e) => setArquivo(e.target.files?.[0] || null)}
                 className="block w-full text-[12px] text-neutral-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-neutral-900 file:text-white file:text-[12px] file:cursor-pointer mb-2" />
          <label className="text-[10px] uppercase tracking-widest text-neutral-500">Digite RESTAURAR para liberar</label>
          <input value={confirmacao} onChange={(e) => setConfirmacao(e.target.value)} data-testid="bk-confirm"
                 placeholder="RESTAURAR" className={inputCls + " mt-1 mb-3"} />
          <button onClick={restaurar} disabled={restaurando || !arquivo || confirmacao.trim().toUpperCase() !== "RESTAURAR"}
                  data-testid="bk-restaurar-btn"
                  className="btn-action h-10 px-4 text-sm font-medium rounded-lg bg-rose-600 text-white flex items-center gap-1.5 disabled:opacity-40">
            {restaurando ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
            {restaurando ? "Restaurando..." : "Restaurar deste arquivo"}
          </button>
        </section>
      </div>

      {/* Backup automático por e-mail */}
      <section className="card-soft rounded-2xl p-5" data-testid="bk-agendamento">
        <div className="flex items-center gap-2 mb-1">
          <Mail size={18} className="text-[color:var(--brand-blue)]" />
          <h2 className="font-serif-display text-lg">Backup automático por e-mail</h2>
          <div className="ml-auto flex items-center gap-2">
            <span className="text-[11px] text-neutral-500">{agForm.ativo ? "Ativo" : "Inativo"}</span>
            <Switch checked={agForm.ativo} onCheckedChange={(v) => setAgF("ativo", v)} data-testid="bk-ag-ativo" />
          </div>
        </div>
        <p className="text-[12px] text-neutral-500 mb-3">O sistema gera o backup completo e envia por e-mail na frequência e hora escolhidas (horário de Brasília).</p>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">E-mail destino</label>
            <input type="email" data-testid="bk-ag-email" value={agForm.email_destino} onChange={(e) => setAgF("email_destino", e.target.value)} placeholder="voce@exemplo.com" className={inputCls + " mt-1"} />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Frequência</label>
            <select data-testid="bk-ag-freq" value={agForm.frequencia} onChange={(e) => setAgF("frequencia", e.target.value)} className={inputCls + " mt-1"}>
              <option value="diaria">Diária</option>
              <option value="semanal">Semanal</option>
              <option value="mensal">Mensal</option>
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-neutral-500">Hora (HH:MM)</label>
            <div className="flex items-center gap-1 mt-1">
              <input type="number" min="0" max="23" data-testid="bk-ag-hora" value={agForm.hora} onChange={(e) => setAgF("hora", parseInt(e.target.value) || 0)} className={inputCls + " w-20"} />
              <span className="text-neutral-400">:</span>
              <input type="number" min="0" max="59" value={agForm.minuto} onChange={(e) => setAgF("minuto", parseInt(e.target.value) || 0)} className={inputCls + " w-20"} />
            </div>
          </div>
          {agForm.frequencia === "semanal" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Dia da semana</label>
              <select value={agForm.dia_semana} onChange={(e) => setAgF("dia_semana", parseInt(e.target.value))} className={inputCls + " mt-1"}>
                {["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"].map((d, i) => (<option key={i} value={i}>{d}</option>))}
              </select>
            </div>
          )}
          {agForm.frequencia === "mensal" && (
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-500">Dia do mês (1–28)</label>
              <input type="number" min="1" max="28" value={agForm.dia_mes} onChange={(e) => setAgF("dia_mes", parseInt(e.target.value) || 1)} className={inputCls + " mt-1"} />
            </div>
          )}
        </div>
        <details className="mt-3">
          <summary className="text-[12px] text-neutral-500 cursor-pointer select-none">Servidor de envio (SMTP) — avançado</summary>
          <div className="grid md:grid-cols-2 gap-3 mt-2">
            <div><label className="text-[10px] uppercase tracking-widest text-neutral-500">SMTP host</label><input value={agForm.smtp_host} onChange={(e) => setAgF("smtp_host", e.target.value)} className={inputCls + " mt-1"} /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-neutral-500">Porta</label><input type="number" value={agForm.smtp_porta} onChange={(e) => setAgF("smtp_porta", parseInt(e.target.value) || 587)} className={inputCls + " mt-1"} /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-neutral-500">Usuário (remetente)</label><input value={agForm.smtp_user} onChange={(e) => setAgF("smtp_user", e.target.value)} className={inputCls + " mt-1"} /></div>
            <div><label className="text-[10px] uppercase tracking-widest text-neutral-500">Senha</label><input type="password" placeholder={ag?.smtp_senha_set ? "•••• (salvo)" : "senha"} value={agForm.smtp_senha} onChange={(e) => setAgF("smtp_senha", e.target.value)} className={inputCls + " mt-1"} /></div>
            <div className="md:col-span-2"><label className="text-[10px] uppercase tracking-widest text-neutral-500">Nome do remetente</label><input value={agForm.smtp_from_nome} onChange={(e) => setAgF("smtp_from_nome", e.target.value)} className={inputCls + " mt-1"} /></div>
          </div>
        </details>
        {ag?.ultimo_status && <p className="text-[11px] text-neutral-500 mt-3 flex items-center gap-1.5"><Clock size={12} /> Último: {ag.ultimo_status}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <button onClick={saveAg} disabled={savingAg} data-testid="bk-ag-salvar" className="btn-action btn-salvar">
            {savingAg ? <Loader2 size={16} className="animate-spin" /> : <Mail size={16} />}{savingAg ? "Salvando..." : "Salvar agendamento"}
          </button>
          <button onClick={enviarAgora} disabled={enviandoAg} data-testid="bk-ag-enviar"
                  className="btn-ghost h-10 px-4 text-sm font-medium flex items-center gap-1.5">
            {enviandoAg ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}{enviandoAg ? "Enviando..." : "Enviar agora (teste)"}
          </button>
        </div>
      </section>

      {/* Histórico de backups */}
      <section className="card-soft rounded-xl overflow-hidden" data-testid="bk-historico">
        <div className="px-4 pt-4 pb-2 flex items-center justify-between">
          <h2 className="font-serif-display text-lg">Histórico de backups</h2>
          <span className="text-[11px] text-neutral-500">{total} registro(s)</span>
        </div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-[10px] uppercase tracking-widest">Quando</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tipo</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Usuário</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tabelas</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Registros</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Tamanho</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest">Arquivo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map((l, i) => (
                <TableRow key={i} data-testid={`bk-log-${i}`}>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(l.criado_em).toLocaleString("pt-BR")}</TableCell>
                  <TableCell>
                    {l.tipo === "restore"
                      ? <span className="chip text-[10px] py-0.5" style={{ background: "rgba(244,63,94,0.18)", color: "#9F1239" }}>Restauração</span>
                      : <span className="chip text-[10px] py-0.5" style={{ background: "rgba(16,185,129,0.18)", color: "#047857" }}>Backup</span>}
                  </TableCell>
                  <TableCell className="text-xs">{l.usuario}</TableCell>
                  <TableCell className="text-xs">{l.total_tabelas}</TableCell>
                  <TableCell className="text-xs">{(l.total_registros ?? 0).toLocaleString("pt-BR")}</TableCell>
                  <TableCell className="text-xs">{fmtBytes(l.tamanho_bytes)}</TableCell>
                  <TableCell className="text-xs text-neutral-500 max-w-[220px] truncate" title={l.arquivo}>{l.arquivo}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-neutral-500 py-6">Nenhum backup ainda.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </div>
        {totalPages > 1 && (
          <div className="flex items-center justify-between gap-2 p-3">
            <button disabled={page <= 1} onClick={() => loadLogs(page - 1)} data-testid="bk-prev"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
              <ChevronLeft size={13} /> Anterior
            </button>
            <span className="text-[11px] text-neutral-600">Página <b>{page}</b> de <b>{totalPages}</b></span>
            <button disabled={page >= totalPages} onClick={() => loadLogs(page + 1)} data-testid="bk-next"
                    className="btn-ghost h-9 px-3 text-[12px] font-medium flex items-center gap-1.5 disabled:opacity-40">
              Próxima <ChevronRight size={13} />
            </button>
          </div>
        )}
      </section>
    </div>
  );
}

function Mini({ label, value, accent }) {
  return (
    <div className={`rounded-lg p-2.5 min-w-[140px] ${accent ? "bg-blue-50" : "bg-neutral-50"}`}>
      <div className="text-[9px] uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="font-serif-display text-lg mt-0.5 leading-tight">{value}</div>
    </div>
  );
}
